import { QueryFunctionContext, QueryKey, UseQueryOptions } from '@tanstack/react-query';

export interface UseDataQueryKeyAndFn<T = any> {
  queryKey: UseDataQueryKey;
  queryFn: (context?: QueryFunctionContext<QueryKey, never>) => Promise<T>;
}

export interface UseDataQuery<T = any> extends UseDataQueryKeyAndFn<T> {
  staleTime?: number;
  refetchOnMount?: boolean;
  gcTime?: number;
  teamId?: string;
  keepPreviousData?: boolean;
}

export type UseDataQueryKey = readonly [
  queryKeyEnum: DataQueryKey | string,
  uniqueId?: string,
  ...others: Array<any>,
];

export type UseDataQueryOptions<T = any> = Pick<
  UseQueryOptions<T, unknown, T, readonly unknown[]>,
  | 'enabled'
  | 'initialData'
  | 'networkMode'
  | 'retryDelay'
  | 'retry'
  | 'refetchInterval'
  | 'refetchOnWindowFocus'
> & {
  params?: any;
};

export interface UseDataQueryHandleRefetchParams {
  ignoreOnlineValidation?: boolean;
}

/**
 * @deprecated crie funções específicas para criação das keys
 */
export enum DataQueryKey {
  BRAND_GENERAL_SETTINGS = 'brands/general-settings',
  ORDER = 'orders/:id',
  ORDER_CONDITIONS = 'orders/:id/order-conditions',
  ORDER_CALCULATE_SUMMARY = 'orders/:orderId/calculate',
  ORDER_VALIDATE = 'orders/:id/validate',
  ORDER_VALIDATE_GENERAL_DATA = 'orders/:id/validate-general-data',
  ORDER_VALIDATE_PRODUCTS = 'orders/:id/validate-products',
  ORDER_VALIDATE_CONDITIONS = 'orders/:id/validate-conditions',
  CLASSIFICATION_TYPES = 'classification-types',
  CLASSIFICATION_TYPES_NO_RBAC = 'classification-types/no-rbac',
  ORDER_CONDITIONS_CLASSIFICATION_TYPES = 'orders/order-conditions/classification-types',
  DELIVERY_WINDOWS = 'delivery-windows',
  PRICE_TABLES = 'price-tables',
  CHAT_NOTIFICATIONS = 'chats/notifications',
  FIND_GROUPED_CHATS = 'chats/grouped',
  CHAT_TO_UPDATE = 'chats/:id/to-update',
  STATES = 'addresses/states',
  CITIES = 'addresses/cities',
  SALES_REPRESENTATIVES = 'sales-representatives',
  SHARED_ENTITIES = 'shared-entities',
  CUSTOMER_GROUPS = 'customer-groups',
  CUSTOMERS = 'customers',
  SIMPLE_ORDERS = 'orders/simple',
  MAX_SUBTOTAL_OF_ORDERS = 'orders/max-subtotal',
  ORDER_COLLECTIONS = 'collections/orders',
  COLLECTIONS = 'collections',
  PRODUCT_GRID = 'orders/order-products/:orderProductId/grid-v2',
  PRODUCT_COLOR_SELLOUT = 'reports/sellout/commerce',
  PRODUCT_TOTALS_COLOR_SELLOUT = 'reports/sellout/commerce/totals',
  COLORS = 'colors',
  CATEGORIES = 'categories',
  CLASSIFICATIONS = 'classifications',
  CUSTOMERS_CLASSIFICATIONS = 'customers-classifications',
  EVENTS = 'events',
  PRODUCT_DETAILS_COMBINATIONS = 'product-details-combinations',
  PRODUCT_DETAILS_COUNT = 'product-details-count',
  PRODUCT_DETAILS_EMPTY_AVAILABILITIES = 'product-details-empty-availabilities',
  PRODUCT_DETAILS_SIZES = 'product-details-sizes',
  PRODUCT_DETAILS_SUMMARY = 'product-details-summary',
  PRODUCT_DETAILS_FILTER = 'product-details-filter',
  PRODUCT_DETAILS_PRODUCT_COLOR = 'product-details-product-color',
  CITIES_FILTER_OPTIONS = 'filter-options/cities',
  CUSTOMERS_FILTER_OPTIONS = 'filter-options/customers',
  PRICE_TABLES_FILTER_OPTIONS = 'filter-options/price-tables',
  STATES_FILTER_OPTIONS = 'filter-options/states',
  SUGGESTED_PRODUCTS = 'suggested-products',
  CUSTOMER_GROUPS_FILTER_OPTIONS = 'filter-options/customer-groups',
  SALES_REPRESENTATIVES_FILTER_OPTIONS = 'filter-options/sales-representatives',
  SALES_TARGETS_FILTER_OPTIONS = 'filter-options/sales-targets',
  SUGGESTED_COPIES_FILTER_OPTIONS = 'filter-options/suggested-copies',
  ORDER_VOUCHER_FILTER_OPTIONS = 'filter-options/order-vouchers',
  ORDERS_FILTER_OPTIONS = 'filter-options/orders',
  SELLERS_FILTER_OPTIONS = 'filter-options/sellers',
  COLLECTIONS_FILTER_OPTIONS = 'filter-options/collections',
  PRODUCTS_FILTER_OPTIONS = 'filter-options/products',
  KITS_FILTER_OPTIONS = 'filter-options/kits',
  COLORS_FILTER_OPTIONS = 'filter-options/colors',
  REFERENCE_COLORS_FILTER_OPTIONS = 'filter-options/reference-colors',
  REFERENCE_COLORS = 'colors/reference-colors',
  CATEGORIES_FILTER_OPTIONS = 'filter-options/categories',
  DELIVERY_WINDOWS_FILTER_OPTIONS = 'filter-options/delivery-windows',
  SIZES_FILTER_OPTIONS = 'filter-options/sizes',
  CLASSIFICATIONS_FILTER_OPTIONS = 'filter-options/classifications',
  CLASSIFICATIONS_NO_RBAC_FILTER_OPTIONS = 'filter-options/classifications/no-rbac',
  SALES_OPPORTUNITY_FILTER_OPTIONS = 'filter-options/sales-opportunities',
  INVOICES_FILTER_OPTIONS = 'filter-options/invoices',
  MAX_CONVERSION_VALUE = 'sales-opportunities/max-conversion-values',
  MAX_ACHIEVED_VALUE = 'sales-opportunities/max-achieved-values',
  USER_CONTEXT_PREFERENCES = 'users/user-context-preferences',
  ORDER_BOOK_SALES_OPPORTUNITIES = 'order-book/sales-opportunities',
  ORDER_BOOK_SALES_OPPORTUNITIES_CATEGORIES = 'order-book/sales-opportunities/categories',
  ORDER_BOOK_SALES_OPPORTUNITIES_CATEGORIES_CONVERSIONS = 'order-book/sales-opportunities/categories-conversions',
  ORDER_BOOK_SALES_OPPORTUNITIES_CONVERSIONS = 'order-book/sales-opportunities/conversions',
  BRAND_SALES_OPPORTUNITY_CONFIG = 'sales-opportunities/brand-sales-opportunity-config',
  CASES_FILTER_OPTIONS = 'filter-options/cases',
  CASE_STATUS_FILTER_OPTIONS = 'filter-options/case-status',
  CASE_AUTHORS_FILTER_OPTIONS = 'filter-options/case-author',
  CASE_RESPONSIBLES_FILTER_OPTIONS = 'filter-options/case-responsible',
  CONSULTANTS_FILTER_OPTIONS = 'filter-options/consultants',
  CONSULTANTS = 'consultants',
  USE_INFINITY_COUNT = 'use-infinity-count',
  HOME_CONNECTIONS_COUNT = 'connections/count',
  HOME_STOREFRONT = 'storefront/:id',
  HOME_SHOWROOM = 'showroom/:id',
  PRODUCT_SEARCH_COLORS = '/product-search/colors',
  PRODUCT_SEARCH_COLOR_AVAILABILITIES = '/product-search/availability',
  PRODUCT_COLOR_AVAILABLE_DELIVERY_WINDOWS = 'product-color/:productColorId/available-delivery-windows',
  KITS = 'kits',
  KIT = 'kits/:id',
  KIT_PRODUCTS_LIST = 'kits/:id/kit-products',
  KIT_DETAILS = 'kit-details/:kitId',
  KIT_COMBINATIONS = '/kit-details/:kitId/combinations/page',
  KIT_COMBINATIONS_COUNT = '/kit-details/:kitId/combinations/count',
  ACCOUNTS_RECEIVABLE_INSTALLMENTS_FILTER_OPTIONS = 'filter-options/accounts-receivable-installments',
  PRODUCTS_MIN_MAX_PRICES = 'products/min-max-prices',
  SKU_CODES_FILTER_OPTIONS = 'filter-options/sku-codes',
  PROMOTION_CODES_FILTER_OPTIONS = 'filter-options/promotion-codes',
  PRODUCT_DETAILS_COMBINATIONS_VALIDATION = 'product-details-combinations-validation',
}
